import React, { createContext, useContext, useState } from 'react'

export type SafetynetModalState = {
  isOpen: boolean
  onConfirm: (value?: unknown) => void
  onCancel: (value?: unknown) => void
  tagPrefix?: string
}

type SafetynetModalContextValue = SafetynetModalState & {
  showSafetynetModal: (tagPrefix?: string) => Promise<boolean>
}

export const SafetynetModalContext = createContext(
  {} as SafetynetModalContextValue,
)

export const defaultSafetynetModalProviderValues = {
  isOpen: false,
  onConfirm: /* istanbul ignore next */ (): null => null,
  onCancel: /* istanbul ignore next */ (): null => null,
  tagPrefix: '',
}

export const SafetynetModalProvider: React.FC = ({ children }) => {
  const [safetynetState, setSafetynetState] = useState<SafetynetModalState>(
    defaultSafetynetModalProviderValues,
  )

  const onConfirm = (): boolean => {
    setSafetynetState({
      ...safetynetState,
      isOpen: false,
    })
    return true
  }

  const onReject = (lastFocusEl: HTMLElement | null): boolean => {
    setSafetynetState({
      ...safetynetState,
      isOpen: false,
    })
    requestAnimationFrame(() => /* istanbul ignore next */ lastFocusEl?.focus())
    return false
  }

  // example usage:
  // const shouldNavigate = await showSafetynetModal()
  // if (shouldNavigate) {...perform yes action}
  // else {...perform no action}
  const showSafetynetModal = (tagPrefix?: string): Promise<boolean> => {
    const { activeElement } = document
    return new Promise((resolve, reject) => {
      setSafetynetState({
        isOpen: true,
        onConfirm: resolve,
        onCancel: reject,
        tagPrefix,
      })
    }).then(
      onConfirm,
      /* istanbul ignore next - ignore coverage of anonymous function*/ () =>
        onReject(activeElement as HTMLElement),
    )
  }

  return (
    <SafetynetModalContext.Provider
      value={{ ...safetynetState, showSafetynetModal }}
    >
      {children}
    </SafetynetModalContext.Provider>
  )
}

export const useSafetynetModal = (): SafetynetModalContextValue =>
  useContext(SafetynetModalContext)
